/* eslint-disable camelcase */
import { createAction } from 'redux-actions';
import type { Store } from 'redux';
import { HttpClient } from '@piwikpro/http-crate';
import {
  Service,
  inject,
} from '@piwikpro/platform';
import { NotifyOnFail, NotifyAboutUpdate, Notifications } from '@piwikpro/notification-crate';
import { ITrafficMonitorData, TrafficMonitorResponse } from './types';

export const trafficUsageFetchInitialized = createAction(
  '@platform/query/trafficUsage/get/init',
);
export const trafficUsageFetchSucceeded = createAction<
ITrafficMonitorData[], ITrafficMonitorData[]
>(
  '@platform/query/trafficUsage/get/success',
  (payload: ITrafficMonitorData[]) => payload,
);
export const trafficUsageFetchFailed = createAction(
  '@platform/query/trafficUsage/get/failed',
);

@Service()
export class TrafficMonitor {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFailInterceptor: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdateInterceptor: NotifyAboutUpdate,
    @inject('NotificationCrate.notifications') private readonly notifications: Notifications,
    @inject('config') private readonly config: any,
  ) {}

  async getTrafficUsage(
    { from, to }: { from: string, to?: string },
  ) : Promise<TrafficMonitorResponse> {
    this.store.dispatch(trafficUsageFetchInitialized());

    try {
      const response: TrafficMonitorResponse = await this.httpClient.request({
        endpoint: `${this.config.get('TRAFFIC_MONITOR_URL')}/v2/usage?from=${from}${to ? `&to=${to}` : ''}`,
        method: 'GET',
      })
        .intercept(this.notifyOnFailInterceptor)
        .jsonApi()
        .send();

      this.store.dispatch(trafficUsageFetchSucceeded(response.data));

      return response;
    } catch (err) {
      this.store.dispatch(trafficUsageFetchFailed());

      throw err;
    }
  }

  async downloadUsagePerAppReport(): Promise<void> {
    const notificationId = this.notifications.push({
      text: 'administration:platform.license.traffic.usage.report.notification.pending',
      type: 'pending',
      autoCloseSeconds: 0,
    });

    try {
      const csv = await this.httpClient.request({
        endpoint: `${this.config.get('TRAFFIC_MONITOR_URL')}/v1/usage-per-app`,
        method: 'GET',
        headers: {
          Accept: 'text/csv',
        },
      }).text().send<string>();

      this.notifications.update(notificationId, {
        text: 'administration:platform.license.traffic.usage.report.notification.success',
        type: 'success',
        autoCloseSeconds: 5,
      });
      this.triggerCsvDownload(csv);
    } catch {
      this.notifications.update(notificationId, {
        text: 'administration:platform.license.traffic.usage.report.notification.error',
        type: 'error',
        autoCloseSeconds: 5,
      });
    }
  }

  private triggerCsvDownload(csv: string) {
    const isoDate = new Date().toISOString().slice(0, 10);
    const fileName = `traffic-per-apps-${isoDate}.csv`;
    const blob = new Blob([csv], { type: 'text/csv' });

    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);

      return;
    }

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  getTrafficEventTypes() {
    return [
      { id: 1, name: 'Page view', key: 'custom-reports:debugger.event.type.visit.title' },
      { id: 2, name: 'Outlink', key: 'custom-reports:debugger.event.type.outlink.title' },
      { id: 3, name: 'Download', key: 'custom-reports:debugger.event.type.download.title' },
      { id: 4, name: 'Search', key: 'custom-reports:debugger.event.type.search.title' },
      { id: 5, name: 'Custom event', key: 'custom-reports:debugger.event.type.custom-event.title' },
      { id: 6, name: 'Content impression', key: 'custom-reports:debugger.event.type.content-impression.title' },
      { id: 7, name: 'Content interaction', key: 'custom-reports:debugger.event.type.content-interaction.title' },
      { id: 8, name: 'Goal conversion', key: 'custom-reports:debugger.event.type.goal-conversion.title' },
      { id: 9, name: 'Order', key: 'custom-reports:debugger.event.type.ecommerce-conversion.title' },
      { id: 10, name: 'Abandoned cart', key: 'custom-reports:debugger.event.type.abandoned-cart.title' },
      { id: 11, name: 'SharePoint', key: 'custom-reports:debugger.event.type.sharepoint.title' },
      { id: 12, name: 'Consent form impression', key: 'custom-reports:debugger.event.type.consent-form-impressions.title' },
      { id: 13, name: 'Consent form click', key: 'custom-reports:debugger.event.type.consent-form-click.title' },
      { id: 14, name: 'Consent decision', key: 'custom-reports:debugger.event.type.consent-decision.title' },
      { id: 15, name: 'Custom ping', key: 'custom-reports:debugger.event-type.custom-ping' },
      { id: 16, name: 'Cart update', key: 'custom-reports:debugger.event-type.cart-update' },
      { id: 17, name: 'Broken event', key: 'custom-reports:debugger.event-type.broken-event' },
      { id: 18, name: 'Excluded event', key: 'custom-reports:debugger.event-type.excluded-event' },
      { id: 19, name: 'Heartbeat', key: 'custom-reports:debugger.event-type.heartbeat-ping' },
      { id: 20, name: 'Deanonymization', key: 'custom-reports:debugger.event-type.deanonimization-ping' },
      { id: 21, name: 'Page performance', key: 'custom-reports:debugger.event-type.page-performance-ping' },
      { id: 22, name: 'Product detail view', key: 'custom-reports:debugger.event-type.product-detail-view' },
      { id: 23, name: 'Add to cart', key: 'custom-reports:debugger.event-type.add-to-cart' },
      { id: 24, name: 'Remove from cart', key: 'custom-reports:debugger.event-type.remove-from-cart' },
    ];
  }
}
